import { useEffect, useRef, useState } from "react";
import { MapInstance, MapGL } from "../../../types/map";

interface UserLocationMarkerProps {
  map: MapInstance | null;
  mapgl: MapGL | null;
  isMapReady: boolean;
  position: [number, number] | null;
  heading: number | null;
}

export const UserLocationMarker: React.FC<UserLocationMarkerProps> = ({
  map,
  mapgl,
  isMapReady,
  position,
  heading,
}) => {
  const markerRef = useRef<any | null>(null);
  const animationRef = useRef<number | null>(null);
  const lastPositionRef = useRef<[number, number] | null>(null);
  const lastHeadingRef = useRef<number | null>(null); // Relative rotation
  const targetPositionRef = useRef<[number, number] | null>(null);
  const targetHeadingRef = useRef<number | null>(null); // Absolute heading
  const [mapBearing, setMapBearing] = useState(0);

  const createSvg = () => `
    <svg width="32" height="61" viewBox="0 0 32 61" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 4H32L20 44H12L0 4Z" fill="#7E64FF" opacity="0.6"/>
      <circle cx="16" cy="45" r="12" fill="#7E64FF" stroke="white" stroke-width="4"/>
    </svg>
  `;

  const svgToDataUrl = (svg: string) =>
    `data:image/svg+xml;base64,${btoa(svg)}`;

  // Интерполяция углов
  const interpolateAngle = (start: number, end: number, t: number): number => {
    let diff = end - start;
    while (diff > 180) diff -= 360;
    while (diff < -180) diff += 360;
    return start + diff * t;
  };

  // Отслеживание bearing карты
  useEffect(() => {
    if (!map || !isMapReady) return;

    const updateBearing = () => {
      try {
        const bearing = (map as any).getBearing?.() || 0;
        setMapBearing(bearing);
      } catch (error) {
        // Игнор
      }
    };

    updateBearing();
    try {
      map.on("rotate", updateBearing);
      map.on("rotateend", updateBearing);
    } catch (error) {
      // Игнор
    }

    return () => {
      try {
        map.off("rotate", updateBearing);
        map.off("rotateend", updateBearing);
      } catch (error) {
        // Игнор
      }
    };
  }, [map, isMapReady]);

  // Создание маркера (разово)
  useEffect(() => {
    if (!map || !mapgl || !isMapReady || !position) return;
    if (markerRef.current) return;

    console.log("📍 Создаём маркер пользователя");

    const relativeHeading = (heading ?? 0) - mapBearing;

    try {
      markerRef.current = new mapgl.Marker(map, {
        coordinates: position,
        icon: svgToDataUrl(createSvg()),
        size: [32, 61],
        anchor: [16, 45],
        rotation: relativeHeading,
        opacity: 1,
      });

      lastPositionRef.current = position;
      lastHeadingRef.current = relativeHeading;
      targetPositionRef.current = position;
      targetHeadingRef.current = heading;
    } catch (error) {
      console.warn("Ошибка создания маркера:", error);
    }
  }, [map, mapgl, isMapReady, position]);

  // Анимация
  useEffect(() => {
    if (!markerRef.current) return;

    if (position) targetPositionRef.current = position;
    if (heading !== null && heading !== undefined)
      targetHeadingRef.current = heading;

    if (!animationRef.current) {
      const SPEED = 0.15;

      const animate = () => {
        if (!markerRef.current) return;

        const currentPos = lastPositionRef.current;
        const targetPos = targetPositionRef.current;
        const currentRelative = lastHeadingRef.current ?? 0;
        const targetAbsolute = targetHeadingRef.current ?? 0;
        const targetRelative = targetAbsolute - mapBearing;

        let updated = false;

        // Движение
        if (currentPos && targetPos) {
          const dx = targetPos[0] - currentPos[0];
          const dy = targetPos[1] - currentPos[1];
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance > 0.000001) {
            const newLng = currentPos[0] + dx * SPEED;
            const newLat = currentPos[1] + dy * SPEED;
            const newPos: [number, number] = [newLng, newLat];

            try {
              markerRef.current.setCoordinates(newPos);
              lastPositionRef.current = newPos;
              updated = true;
            } catch (error) {
              console.warn("Ошибка позиции:", error);
            }
          }
        }

        // Поворот
        const angleDiff = Math.abs(
          ((targetRelative - currentRelative + 540) % 360) - 180
        );
        if (angleDiff > 0.5) {
          const newRelative = interpolateAngle(
            currentRelative,
            targetRelative,
            SPEED
          );

          try {
            markerRef.current.setRotation(newRelative);
            lastHeadingRef.current = newRelative;
            updated = true;
          } catch (error) {
            console.warn("Ошибка поворота:", error);
          }
        }

        if (updated) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          animationRef.current = null;
        }
      };

      animationRef.current = requestAnimationFrame(animate);
    }
  }, [position, heading, mapBearing]);

  // Очистка
  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (markerRef.current) {
        try {
          markerRef.current.destroy();
          markerRef.current = null;
        } catch (error) {
          console.warn("Ошибка удаления:", error);
        }
      }
    };
  }, []);

  return null;
};
